import React from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Printer, Smartphone } from 'lucide-react';

export const ProceduresTab = ({ dashboardData, filteredProcedures, fmtDateUS, handleEditProcedure, handleEmailPDF, handleOpenProcedure, handlePrintProcedure, printPatientReport, safeText, showSmsSelectionModal }) => (
  <>
    <div className="space-y-4">
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => printPatientReport({
            tabKey: 'procedures',
            reportTitle: 'Procedures Report',
            subtitle: 'Patient procedure history & status',
            itemNoun: 'procedure',
            items: filteredProcedures,
            headerColor: '#16a34a',
            columns: [
              { key: 'name',   label: 'Procedure' },
              { key: 'dentist', label: 'Provider' },
              { key: 'performed', label: 'Performed' },
              { key: 'followUp', label: 'Follow-Up' },
              { key: 'channel', label: 'Channel' },
              { key: 'status', label: 'Status' },
            ],
            rowFor: (p) => {
              const statusBadge =
                p.status === 'delivered'
                  ? '<span class="badge badge-green">DELIVERED</span>'
                  : p.status === 'second'
                  ? '<span class="badge badge-amber">SECOND</span>'
                  : '<span class="badge badge-gray">ACTIVE</span>';
              const channels = [];
              if (p.emailSent) channels.push(`Email${p.emailSentCount > 1 ? ` ×${p.emailSentCount}` : ''}`);
              if (p.smsSentAt) channels.push('SMS');
              return [
                `<strong>${safeText(p.procedureName)}</strong>`,
                safeText(p.dentistName),
                fmtDateUS(p.performedDate),
                fmtDateUS(p.followUpDate),
                channels.join(', ') || '—',
                statusBadge,
              ];
            },
            stats: (items) => [
              { label: 'Total Procedures', value: items.length },
              { label: 'Delivered', value: items.filter((x) => x.status === 'delivered').length, color: '#16a34a' },
              { label: 'Active', value: items.filter((x) => x.status !== 'delivered').length, color: '#0586ba' },
            ],
          })}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 border border-green-200 rounded-md"
          data-testid="procedures-print-report-btn"
        >
          <Printer className="h-3.5 w-3.5" />
          Print Procedures Report
        </button>
      </div>
      {filteredProcedures.map((procedure) => (
        <div key={procedure.id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
          <div className="flex-1">
            <p className="font-medium text-lg">{procedure.procedureName}</p>
            <p className="text-base text-gray-600">
              {procedure.dentistName && procedure.dentistName.startsWith('Dr.') 
                ? procedure.dentistName 
                : `Dr. ${procedure.dentistName}`}
            </p>
            <p className="text-sm text-gray-500">
              Patient: {procedure.patientName || ''}
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <div className="text-right mr-3">
              <Badge 
                variant="outline" 
                className={`mb-1 ${
                  procedure.status === 'second' 
                    ? 'bg-purple-100 text-purple-800 border-purple-300'
                    : procedure.status === 'delivered' 
                    ? 'bg-blue-100 text-blue-800 border-blue-300' 
                    : 'bg-green-100 text-green-800 border-green-300'
                }`}
              >
                {procedure.status === 'second' ? 'Second' : 
                 procedure.status === 'delivered' ? 'Delivered' : 'Active'}
              </Badge>
              {procedure.emailSent && (
                <Badge 
                  variant="outline" 
                  className="mb-1 ml-1 bg-green-100 text-green-800 border-green-300"
                  title={`Email sent ${procedure.emailSentCount > 1 ? procedure.emailSentCount + ' times. ' : ''}Last sent: ${procedure.lastEmailSentAt ? new Date(procedure.lastEmailSentAt).toLocaleString() : new Date(procedure.emailSentAt).toLocaleString()}`}
                >
                  ✉️ Email Sent{procedure.emailSentCount > 1 ? ` (${procedure.emailSentCount}x)` : ''}
                </Badge>
              )}
              {procedure.smsSentAt && (
                <Badge 
                  variant="outline" 
                  className="mb-1 ml-1 bg-blue-100 text-blue-800 border-blue-300"
                  title={`Text sent: ${new Date(procedure.smsSentAt).toLocaleString()}`}
                >
                  📱 Text Sent
                </Badge>
              )}
              {procedure.followUpStatus && (
                <Badge 
                  variant="outline" 
                  className={`mb-1 ml-1 text-xs ${
                    procedure.followUpStatus === 'sent' 
                      ? 'bg-purple-100 text-purple-800 border-purple-300' 
                      : procedure.followUpStatus === 'scheduled'
                      ? 'bg-yellow-100 text-yellow-800 border-yellow-300'
                      : 'bg-gray-100 text-gray-800 border-gray-300'
                  }`}
                >
                  {procedure.followUpStatus === 'sent' ? 'Follow-up Sent' : 
                   procedure.followUpStatus === 'scheduled' ? 'Follow-up Scheduled' : 
                   'Follow-up Failed'}
                </Badge>
              )}
              <p className="text-sm text-gray-400">
                {new Date(procedure.performedDate).toLocaleDateString()}
              </p>
              {procedure.followUpSentAt && (
                <p className="text-sm text-purple-500">
                  Follow-up: {new Date(procedure.followUpSentAt).toLocaleDateString()}
                </p>
              )}
            </div>
            <div className="flex flex-col space-y-1">
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => handleOpenProcedure(procedure.id)}
                className="text-sm px-3 py-1 h-8"
              >
                View
              </Button>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => handleEditProcedure(procedure.id)}
                className="text-sm px-3 py-1 h-8"
              >
                Edit
              </Button>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => handlePrintProcedure(procedure.id)}
                className="text-sm px-3 py-1 h-8"
              >
                Print
              </Button>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => handleEmailPDF(procedure.id)}
                className="text-sm px-3 py-1 h-8"
              >
                Email
              </Button>
              <Button 
                data-testid={`text-procedure-${procedure.id}`}
                size="sm" 
                variant="outline"
                onClick={() => {
                  const patient = dashboardData?.recentPatients?.find(p => p.id === procedure.patientId);
                  showSmsSelectionModal(
                    procedure.patientId,
                    'procedure',
                    procedure.id,
                    patient?.cellPhone || patient?.phone,
                    `${patient?.firstName || ''} ${patient?.lastName || ''}`.trim(),
                    procedure.name || 'Procedure'
                  );
                }}
                className="text-sm px-3 py-1 h-8"
              >
                <Smartphone className="h-3 w-3 mr-1" />
                Text
              </Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  </>
);
